/**
 * Minimal gettext-style i18n: the English text is the source and the key; other languages
 * provide dictionaries keyed by the English text. Missing translations fall back to English.
 * Placeholders use {name} and are filled from the params object.
 *
 * The current language is module state so that non-React code (stitch engine, workers,
 * format writers) can translate its messages too. Workers receive the language with each request.
 */
import { de } from './de';
import { nb } from './nb';
import { sv } from './sv';

export type Lang = 'en' | 'nb' | 'sv' | 'de';
export const LANGS: { id: Lang; name: string }[] = [
  { id: 'en', name: 'English' },
  { id: 'nb', name: 'Norsk (bokmål)' },
  { id: 'sv', name: 'Svenska' },
  { id: 'de', name: 'Deutsch' },
];
export const DEFAULT_LANG: Lang = 'en';

const dictionaries: Record<Lang, Record<string, string> | null> = { en: null, nb, sv, de };

let current: Lang = DEFAULT_LANG;

export function setLang(lang: Lang): void {
  current = Object.hasOwn(dictionaries, lang) ? lang : DEFAULT_LANG;
}

export const getLang = (): Lang => current;

export type Params = Record<string, string | number>;

export function interpolate(text: string, params?: Params): string {
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (m, k: string) => (k in params ? String(params[k]) : m));
}

/** Translates an English source text into the current language. */
export function t(en: string, params?: Params): string {
  const dict = dictionaries[current];
  return interpolate((dict && dict[en]) || en, params);
}

/** Locale for number/date formatting in the current language. */
const LOCALES: Record<Lang, string> = { en: 'en-GB', nb: 'nb-NO', sv: 'sv-SE', de: 'de-DE' };
export const locale = (): string => LOCALES[current];

export const fmtInt = (n: number): string => n.toLocaleString(locale());

const STORAGE_KEY = 'stitchcraft.lang';

export function loadStoredLang(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && Object.hasOwn(dictionaries, v)) return v as Lang;
  } catch {
    /* storage unavailable */
  }
  return DEFAULT_LANG;
}

export function storeLang(lang: Lang): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* storage unavailable; the choice lasts for this session */
  }
}
